/**
 * Inventory API (owner: C3): locations, supplier orders, lots, receiving, movements, stocktakes,
 * low stock and reorder points under /api/inventory/ (backend/inventory/api.py). Query keys start
 * with "inventory". Also the extension point for the balance (WeightCaptureContext) and label
 * printing through the labels slice (POST /api/labels/render).
 */
import { keepPreviousData, useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext } from "react";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "@/api/client";
import type { DecimalString, IsoDate, IsoDateTime, MinorUnits, Paged, Uuid } from "@/api/types";

// Types (mirror backend/inventory/schemas.py) ------------------------------------------------------

export type LocationKind = "room" | "rack" | "shelf" | "slot" | "box" | "fridge";
/** `on_order`: ordered, not received yet; it holds no stock until it's received. */
export type LotStatus = "on_order" | "active" | "empty" | "disposed";
export type OrderStatus = "on_order" | "partly_received" | "received" | "cancelled";
export type LotOrigin = "received" | "produced";
export type MovementKind =
  "receipt" | "consumption" | "output" | "transfer" | "adjustment" | "stocktake" | "disposal" | "sample";

export interface LocationRef {
  id: Uuid;
  code: string;
  name: string;
  kind: LocationKind;
  path: string;
}

export interface Location {
  id: Uuid;
  parent_id: Uuid | null;
  kind: LocationKind;
  name: string;
  code: string;
  sort: number;
  notes: string;
  depth: number;
  path: string;
  lot_count: number;
  child_count: number;
}

export interface MaterialRef {
  id: Uuid;
  name: string;
  short_code: string | null;
  kind: string;
  is_private: boolean;
}

export interface VariantRef {
  id: Uuid;
  title: string;
  product_title: string;
  supplier_id: Uuid | null;
  supplier_name: string;
  url: string;
  pack_g: DecimalString | null;
  dilution_fraction: DecimalString | null;
  solvent_text: string;
  last_price_minor: MinorUnits | null;
  last_currency: string;
  last_market: string;
}

export interface SupplierOrderRef {
  id: Uuid;
  supplier_name: string;
  reference: string;
  ordered_at: IsoDate;
}

export interface Lot {
  id: Uuid;
  code: string;
  display_code: string;
  material: MaterialRef;
  status: LotStatus;
  origin: LotOrigin;
  location: LocationRef | null;
  active_fraction: DecimalString;
  carrier_material_id: Uuid | null;
  carrier_name: string;
  carrier_fraction: DecimalString | null;
  /** "10% in DPG", or "" for a neat material. */
  dilution_label: string;
  labelled_qty_g: DecimalString | null;
  balance_g: DecimalString;
  initial_g: DecimalString | null;
  fraction_left: DecimalString | null;
  low_stock: boolean;
  underweight: boolean;
  received_at: IsoDateTime | null;
  opened_at: IsoDateTime | null;
  expires_at: IsoDate | null;
  retest_at: IsoDate | null;
  made_at?: IsoDateTime | null;
  label_details?: LabelFact[];
  supplier_name: string;
  supplier_variant_id: Uuid | null;
  variant_title: string;
  supplier_lot_number: string;
  supplier_order: SupplierOrderRef | null;
  landed_cost_minor: MinorUnits | null;
  currency: string;
  import_job_id: Uuid | null;
  created_at: IsoDateTime;
}

export interface SupplierOrder {
  id: Uuid;
  supplier_id: Uuid | null;
  supplier_name: string;
  /** The supplier's order number, when known. */
  reference: string;
  ordered_at: IsoDate;
  notes: string;
  status: OrderStatus;
  lot_count: number;
  on_order_count: number;
  received_count: number;
  total_cost_minor: MinorUnits | null;
  currency: string;
  import_job_id: Uuid | null;
  created_at: IsoDateTime;
}

export interface SupplierOrderDetail extends SupplierOrder {
  lots: Lot[];
}

export interface Movement {
  id: Uuid;
  kind: MovementKind;
  qty_g: DecimalString;
  balance_after_g: DecimalString | null;
  occurred_at: IsoDateTime;
  created_at: IsoDateTime;
  note: string;
  actor_name: string;
  from_location_code: string | null;
  to_location_code: string | null;
  batch_id: Uuid | null;
  stocktake_session_id: Uuid | null;
  import_job_id: Uuid | null;
  lot_id: Uuid | null;
  lot_code: string | null;
  material_name: string | null;
}

export interface DocumentRef {
  id: Uuid;
  filename: string;
  kind: string;
  role: string;
  created_at: IsoDateTime;
  download_url: string;
}

/** What a picture of a container is of. */
export type PhotoKind = "bottle" | "label" | "packaging" | "paperwork" | "other";
export type PhotoReadingStatus = "none" | "pending" | "done" | "failed";

/** One field a photo suggested for its lot, beside the value the lot holds now. */
export interface PhotoSuggestion {
  value: string;
  confidence: number;
  evidence: string;
  current: string | null;
  differs: boolean;
  photo_ids?: Uuid[];
  requires_review?: boolean;
}

export interface LabelFact {
  key: string;
  label: string;
  value: string;
  evidence: string;
  photo_ids: Uuid[];
  reading_id?: Uuid;
  saved_at?: IsoDateTime;
}

export interface LabelIdentityCheck {
  field: string;
  status: string;
  expected: string[];
  observed: string[];
  evidence: string;
  photo_ids: Uuid[];
  message: string;
}

export interface CombinedLabelReading {
  id: Uuid;
  status: "queued" | "running" | "done" | "failed" | "cancelled" | "stale";
  stale: boolean;
  error: string;
  job_id: Uuid | null;
  source_photo_ids: Uuid[];
  created_at: IsoDateTime;
  finished_at: IsoDateTime | null;
  model: string;
  suggested: Record<string, PhotoSuggestion>;
  additional_facts: LabelFact[];
  identity: { status: "match" | "mismatch" | "unverified"; checks: LabelIdentityCheck[] };
  conflicts: Array<{ field: string; message: string; values: Array<Partial<PhotoSuggestion>> }>;
  warnings: string[];
  note: string;
  requires_review: boolean;
  applied_at: IsoDateTime | null;
}

export interface LotPhoto {
  id: Uuid;
  lot_id: Uuid;
  kind: PhotoKind;
  caption: string;
  position: number;
  taken_at: IsoDateTime | null;
  created_at: IsoDateTime;
  filename: string;
  mime: string;
  size: number;
  /** The image itself, for an <img> (session-authenticated). */
  url: string;
  download_url: string;
  reading_status: PhotoReadingStatus;
  reading_error: string;
  reading_model: string;
  reading_at: IsoDateTime | null;
  reading_note?: string;
  reading_text?: string;
  warnings?: string[];
  suggested?: Record<string, PhotoSuggestion>;
  ocr_status?: PhotoReadingStatus;
  ocr_text?: string;
  ocr_error?: string;
  ocr_at?: IsoDateTime | null;
  ocr_meta?: Record<string, unknown>;
}

export interface LotPhotos {
  photos: LotPhoto[];
  /** Whether a model is configured to read pictures (they are kept either way). */
  reading_available: boolean;
  ocr_available?: boolean;
  label_reading?: CombinedLabelReading | null;
  label_details?: LabelFact[];
}

export interface UploadPhotoBody {
  file: File;
  kind?: PhotoKind;
  caption?: string;
  /** Run RapidOCR on the picture (default true; a combined model reading is requested separately). */
  read?: boolean;
}

export interface MaterialNote {
  id: Uuid;
  material_id: Uuid | null;
  material_name: string | null;
  title: string;
  text: string;
  source: "manual" | "csv" | "formulair" | "molequles";
  import_job_id: Uuid | null;
  created_at: IsoDateTime;
}

export interface Receiving {
  labelled_qty_g: DecimalString | null;
  gross_weight_g: DecimalString | null;
  container_tare_g: DecimalString | null;
  net_received_g: DecimalString | null;
  difference_g: DecimalString | null;
  tolerance_g: DecimalString | null;
  underweight: boolean;
  checked: boolean;
}

export type TareSource = "estimated" | "measured" | "unknown";

export interface LotWeighing {
  id: string;
  recorded_at: IsoDateTime;
  source: "receipt" | "receiving_check" | "stocktake";
  measurement_kind: "gross" | "net";
  gross_weight_g: DecimalString | null;
  container_tare_g: DecimalString | null;
  tare_source: TareSource | null;
  net_weight_g: DecimalString | null;
  net_source: "calculated" | "assumed" | "direct" | "unknown";
  quantity_basis_g: DecimalString | null;
  expected_g: DecimalString | null;
  delta_g: DecimalString | null;
  stock_changed: boolean;
  actor_name: string;
  note: string;
  legacy: boolean;
}

export interface LotDetail extends Lot {
  notes: string;
  made_at: IsoDateTime | null;
  density_g_ml: DecimalString | null;
  producing_batch_id: Uuid | null;
  variant: VariantRef | null;
  price_per_g_minor: DecimalString | null;
  price_per_active_g_minor: DecimalString | null;
  receiving: Receiving;
  container_tare_source: TareSource;
  weighings: LotWeighing[];
  movements: Movement[];
  documents: DocumentRef[];
  photos: LotPhoto[];
  material_notes: MaterialNote[];
  evaluation_count: number;
  reorder_point_g: DecimalString | null;
}

export interface Facet {
  value: string;
  label: string;
  count: number;
}

export interface LotFacets {
  /** Lots on the shelf (in stock or empty); on-order and disposed lots aren't counted. */
  total: number;
  statuses: Facet[];
  origins: Facet[];
  suppliers: Facet[];
  low_stock: number;
  on_order: number;
  open_orders: number;
}

export interface LotFilters {
  q?: string;
  material?: Uuid;
  location?: Uuid;
  /** Without a status: lots in stock and empty ones. */
  status?: LotStatus | "all" | "in_stock";
  origin?: LotOrigin;
  supplier?: string;
  low_stock?: boolean;
  import_job?: Uuid;
  order?: Uuid;
  sort?: string;
  limit?: number;
  offset?: number;
}

export interface ReceiveCheckResult {
  net_received_g: DecimalString;
  labelled_qty_g: DecimalString | null;
  difference_g: DecimalString | null;
  tolerance_g: DecimalString | null;
  underweight: boolean;
  correction_movement: Movement | null;
  lot: LotDetail;
}

export interface WeighedLot {
  lot_id: Uuid;
  code: string;
  display_code: string;
  material_name: string;
  dilution_label: string;
  expected_g: DecimalString;
  remaining_g: DecimalString;
  delta_g: DecimalString;
  weighed_at: IsoDateTime;
  movement_id: Uuid;
}

export interface UnweighedLot {
  lot_id: Uuid;
  code: string;
  display_code: string;
  material_name: string;
  location_code: string | null;
  balance_g: DecimalString;
}

export interface Stocktake {
  id: Uuid;
  started_at: IsoDateTime;
  finished_at: IsoDateTime | null;
  location: LocationRef | null;
  notes: string;
  lots_weighed: number;
  total_delta_g: DecimalString;
  weighed: WeighedLot[];
  unweighed: UnweighedLot[];
}

export interface WeighResult {
  expected_g: DecimalString;
  remaining_g: DecimalString;
  delta_g: DecimalString;
  tare_saved: boolean;
  movement: Movement;
  lot: Lot;
}

export interface LowMaterial {
  material: MaterialRef;
  min_g: DecimalString;
  balance_g: DecimalString;
  lot_count: number;
  /** Labelled grams of its lots still on order (not counted in `balance_g`). */
  on_order_g: DecimalString;
}

export interface LowStock {
  fraction: DecimalString;
  materials: LowMaterial[];
  lots: Lot[];
}

export interface ReorderPoint {
  id: Uuid;
  material: MaterialRef;
  min_g: DecimalString;
  notes: string;
  balance_g: DecimalString;
  is_low: boolean;
}

/** A material hit from GET /api/search (materials group). */
export interface MaterialHit {
  id: Uuid;
  label: string;
  detail?: string;
}

export interface LotIn {
  material_id: Uuid;
  supplier_variant_id?: Uuid | null;
  labelled_qty_g?: DecimalString | null;
  supplier_lot_number?: string;
  received_at?: IsoDate | null;
  opened_at?: IsoDate | null;
  expires_at?: IsoDate | null;
  retest_at?: IsoDate | null;
  location_id?: Uuid | null;
  active_fraction?: DecimalString | null;
  carrier_material_id?: Uuid | null;
  landed_cost_minor?: MinorUnits | null;
  currency?: string;
  notes?: string;
  gross_weight_g?: DecimalString | null;
  container_tare_g?: DecimalString | null;
  record_correction?: boolean;
}

export type LotPatch = Partial<
  Pick<
    LotIn,
    | "supplier_lot_number"
    | "notes"
    | "opened_at"
    | "expires_at"
    | "retest_at"
    | "labelled_qty_g"
    | "active_fraction"
    | "carrier_material_id"
    | "landed_cost_minor"
    | "currency"
  >
> & { container_tare_g?: DecimalString | null; reason?: string };

export interface LocationIn {
  kind: LocationKind;
  name: string;
  code?: string;
  parent_id?: Uuid | null;
  sort?: number;
  notes?: string;
}

// Keys ---------------------------------------------------------------------------------------------

export const inventoryKeys = {
  all: ["inventory"] as const,
  lots: (filters: LotFilters) => ["inventory", "lots", filters] as const,
  facets: ["inventory", "facets"] as const,
  lot: (code: string) => ["inventory", "lot", code] as const,
  orders: (status: OrderListStatus) => ["inventory", "orders", status] as const,
  order: (id: Uuid) => ["inventory", "order", id] as const,
  locations: ["inventory", "locations"] as const,
  stocktakes: ["inventory", "stocktakes"] as const,
  stocktake: (id: Uuid) => ["inventory", "stocktake", id] as const,
  lowStock: ["inventory", "low-stock"] as const,
  reorderPoints: ["inventory", "reorder-points"] as const,
  variantOptions: (materialId: Uuid) => ["inventory", "variant-options", materialId] as const,
  photos: (lotId: Uuid) => ["inventory", "photos", lotId] as const,
};

function useInvalidateInventory() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
}

// Lots ---------------------------------------------------------------------------------------------

export function useLots(filters: LotFilters) {
  return useQuery({
    queryKey: inventoryKeys.lots(filters),
    queryFn: ({ signal }) => apiGet<Paged<Lot>>("/inventory/lots/", { params: { limit: 100, ...filters }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useLotFacets() {
  return useQuery({
    queryKey: inventoryKeys.facets,
    queryFn: ({ signal }) => apiGet<LotFacets>("/inventory/lots/facets", { signal }),
  });
}

/** A lot by its code, as typed, scanned or in a QR link (/lots/T030-24S). */
export function fetchLotByCode(code: string, signal?: AbortSignal) {
  return apiGet<LotDetail>(`/inventory/lots/by-code/${encodeURIComponent(code.trim())}`, { signal });
}

export function useLotByCode(code: string | undefined) {
  return useQuery({
    queryKey: inventoryKeys.lot(code ?? ""),
    queryFn: ({ signal }) => fetchLotByCode(code ?? "", signal),
    enabled: Boolean(code),
    retry: false,
  });
}

export function useCreateLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: LotIn) => apiPost<LotDetail>("/inventory/lots/", body),
    onSettled: invalidate,
  });
}

export function useUpdateLot(lotId: Uuid) {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: LotPatch) => apiPatch<LotDetail>(`/inventory/lots/${lotId}`, body),
    onSettled: invalidate,
  });
}

export function useMoveLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ lotId, locationId, note }: { lotId: Uuid; locationId: Uuid; note?: string }) =>
      apiPost<LotDetail>(`/inventory/lots/${lotId}/move`, { location_id: locationId, note: note ?? "" }),
    onSettled: invalidate,
  });
}

export interface AdjustBody {
  reason: string;
  delta_g?: DecimalString;
  new_balance_g?: DecimalString;
  kind?: "adjustment" | "sample";
}

export function useAdjustLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ lotId, ...body }: AdjustBody & { lotId: Uuid }) =>
      apiPost<LotDetail>(`/inventory/lots/${lotId}/adjust`, body),
    onSettled: invalidate,
  });
}

export function useDisposeLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ lotId, reason }: { lotId: Uuid; reason: string }) =>
      apiPost<LotDetail>(`/inventory/lots/${lotId}/dispose`, { reason }),
    onSettled: invalidate,
  });
}

export interface ReceiveCheckBody {
  gross_weight_g: DecimalString;
  container_tare_g: DecimalString;
  labelled_qty_g?: DecimalString | null;
  record_correction?: boolean;
}

export function useReceiveCheck(lotId: Uuid) {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: ReceiveCheckBody) => apiPost<ReceiveCheckResult>(`/inventory/lots/${lotId}/receive-check`, body),
    onSettled: invalidate,
  });
}

export interface ReceiveLotBody {
  received_at?: IsoDate | null;
  labelled_qty_g?: DecimalString | null;
  gross_weight_g?: DecimalString | null;
  container_tare_g?: DecimalString | null;
  /** Book the weighed amount instead of the labelled one (default true). */
  record_correction?: boolean;
  location_id?: Uuid | null;
  supplier_lot_number?: string | null;
  expires_at?: IsoDate | null;
  /** Dates and strength a photo of the label showed. */
  retest_at?: IsoDate | null;
  made_at?: IsoDate | null;
  active_fraction?: DecimalString | null;
  carrier_material_id?: Uuid | null;
}

/** An on-order container arrives: book it in (weighed or at its label) and put it in stock. */
export function useReceiveLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ lotId, ...body }: ReceiveLotBody & { lotId: Uuid }) =>
      apiPost<LotDetail>(`/inventory/lots/${lotId}/receive`, body),
    onSettled: invalidate,
  });
}

/** Take a container that won't arrive off its order (the lot is removed; it never held stock). */
export function useCancelLot() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ lotId, reason }: { lotId: Uuid; reason?: string }) =>
      apiPost<void>(`/inventory/lots/${lotId}/cancel`, { reason: reason ?? "" }),
    onSettled: invalidate,
  });
}

// Photos of a container, and what a model reads on them --------------------------------------------

export function useLotPhotos(lotId: Uuid | null) {
  return useQuery({
    queryKey: inventoryKeys.photos(lotId ?? ""),
    queryFn: ({ signal }) => apiGet<LotPhotos>(`/inventory/lots/${lotId}/photos`, { signal }),
    enabled: Boolean(lotId),
    // OCR and the combined model reading finish independently in the worker.
    refetchInterval: (query) =>
      query.state.data?.photos.some((photo) => (photo.ocr_status ?? photo.reading_status) === "pending") ||
      ["queued", "running"].includes(query.state.data?.label_reading?.status ?? "")
        ? 2500
        : false,
  });
}

export function useUploadPhoto(lotId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, kind = "bottle", caption = "", read = true }: UploadPhotoBody) =>
      apiUpload<LotPhotos>(`/inventory/lots/${lotId}/photos`, {
        file,
        kind,
        caption,
        read: read ? "true" : "false",
      }),
    // A delayed upload response may predate a removal. Fetch the current list instead of restoring
    // an older full-list response over a deletion that already completed.
    onSettled: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

/** Upload one photo to any lot (used when the lot's id is only known after a form has been saved). */
export function uploadLotPhoto(lotId: Uuid, body: UploadPhotoBody) {
  return apiUpload<LotPhotos>(`/inventory/lots/${lotId}/photos`, {
    file: body.file,
    kind: body.kind ?? "bottle",
    caption: body.caption ?? "",
    read: (body.read ?? true) ? "true" : "false",
  });
}

export function useUpdatePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, ...body }: { photoId: Uuid; kind?: PhotoKind; caption?: string }) =>
      apiPatch<LotPhoto>(`/inventory/photos/${photoId}`, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

const PHOTO_REMOVAL_KEY = ["inventory", "remove-photo"] as const;

/** Receiving must also wait for a removal started inside a camera dialog that has since closed. */
export function usePhotoRemovalPending() {
  return useIsMutating({ mutationKey: PHOTO_REMOVAL_KEY }) > 0;
}

export function useDeletePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: PHOTO_REMOVAL_KEY,
    mutationFn: (photoId: Uuid) => apiDelete(`/inventory/photos/${photoId}`),
    onSuccess: async (_, photoId) => {
      // An older poll must not put a removed picture (or its suggested readings) back in the form.
      await queryClient.cancelQueries({ queryKey: ["inventory", "photos"] });
      queryClient.setQueriesData<LotPhotos>({ queryKey: ["inventory", "photos"] }, (current) =>
        current
          ? {
              ...current,
              photos: current.photos.filter((photo) => photo.id !== photoId),
              label_reading: current.label_reading?.source_photo_ids.includes(photoId)
                ? { ...current.label_reading, stale: true }
                : current.label_reading,
            }
          : current,
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

/** Retry RapidOCR for one photo. The combined reading is requested separately. */
export function useReadPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: Uuid) => apiPost<LotPhoto & { job_id: string }>(`/inventory/photos/${photoId}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

/** Write the values the user kept from a photo's reading onto the lot (the photo is named in its history). */
export function useApplyReading(lotId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ values, photoId }: { values: Record<string, string | null>; photoId?: Uuid }) =>
      apiPost<LotDetail>(`/inventory/lots/${lotId}/apply-reading`, { values, photo_id: photoId ?? null }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useReadLotPhotos(lotId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<CombinedLabelReading>(`/inventory/lots/${lotId}/photos/read`),
    onSuccess: (reading) => {
      queryClient.setQueryData<LotPhotos>(inventoryKeys.photos(lotId), (current) =>
        current ? { ...current, label_reading: reading } : current,
      );
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.photos(lotId) });
    },
  });
}

const LABEL_APPLY_KEY = ["inventory", "apply-label-reading"] as const;

export function useLabelApplyPending() {
  return useIsMutating({ mutationKey: LABEL_APPLY_KEY }) > 0;
}

export function useApplyLabelReading(lotId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: LABEL_APPLY_KEY,
    mutationFn: ({
      readingId,
      ...body
    }: {
      readingId: Uuid;
      fields: string[];
      additional_fact_keys: string[];
      acknowledge_review: boolean;
      field_values?: Record<string, string>;
      additional_fact_values?: Record<string, string>;
    }) => apiPost<LotDetail>(`/inventory/lots/${lotId}/label-readings/${readingId}/apply`, body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

// Supplier orders ----------------------------------------------------------------------------------

/** `open`: something still on order. */
export type OrderListStatus = "open" | "received" | "all";

export function useOrders(status: OrderListStatus = "all", enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.orders(status),
    queryFn: ({ signal }) =>
      apiGet<Paged<SupplierOrder>>("/inventory/orders/", { params: { status, limit: 50 }, signal }),
    enabled,
  });
}

export function useOrder(id: Uuid | undefined) {
  return useQuery({
    queryKey: inventoryKeys.order(id ?? ""),
    queryFn: ({ signal }) => apiGet<SupplierOrderDetail>(`/inventory/orders/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useUpdateOrder(orderId: Uuid) {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: { reference?: string; notes?: string; ordered_at?: IsoDate }) =>
      apiPatch<SupplierOrderDetail>(`/inventory/orders/${orderId}`, body),
    onSettled: invalidate,
  });
}

/** Receive the order's containers still on order (all, or `lot_ids`) at their labelled quantities. */
export function useReceiveOrder(orderId: Uuid) {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: { lot_ids?: Uuid[]; received_at?: IsoDate | null; location_id?: Uuid | null }) =>
      apiPost<SupplierOrderDetail>(`/inventory/orders/${orderId}/receive`, body),
    onSettled: invalidate,
  });
}

export function useVariantOptions(materialId: Uuid | null) {
  return useQuery({
    queryKey: inventoryKeys.variantOptions(materialId ?? ""),
    queryFn: ({ signal }) =>
      apiGet<VariantRef[]>("/inventory/variant-options", { params: { material: materialId }, signal }),
    enabled: Boolean(materialId),
    staleTime: 5 * 60_000,
  });
}

// Locations ----------------------------------------------------------------------------------------

export function useLocations() {
  return useQuery({
    queryKey: inventoryKeys.locations,
    queryFn: ({ signal }) => apiGet<Location[]>("/inventory/locations/", { signal }),
    staleTime: 60_000,
  });
}

export function suggestLocationCode(parentId: Uuid | null, signal?: AbortSignal) {
  return apiGet<{ code: string }>("/inventory/locations/suggest-code", { params: { parent: parentId }, signal });
}

export function useCreateLocation() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: LocationIn) => apiPost<Location>("/inventory/locations/", body),
    onSettled: invalidate,
  });
}

export function useUpdateLocation() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<LocationIn> & { id: Uuid }) =>
      apiPatch<Location>(`/inventory/locations/${id}`, body),
    onSettled: invalidate,
  });
}

export function useDeleteLocation() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (id: Uuid) => apiDelete(`/inventory/locations/${id}`),
    onSettled: invalidate,
  });
}

// Stocktake ----------------------------------------------------------------------------------------

export function useStocktakes() {
  return useQuery({
    queryKey: inventoryKeys.stocktakes,
    queryFn: ({ signal }) =>
      apiGet<Paged<Stocktake>>("/inventory/stocktake/sessions", { params: { limit: 20 }, signal }),
  });
}

export function useStocktake(id: Uuid | null) {
  return useQuery({
    queryKey: inventoryKeys.stocktake(id ?? ""),
    queryFn: ({ signal }) => apiGet<Stocktake>(`/inventory/stocktake/sessions/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useStartStocktake() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: { location_id?: Uuid | null; notes?: string }) =>
      apiPost<Stocktake>("/inventory/stocktake/sessions", body),
    onSettled: invalidate,
  });
}

export function useFinishStocktake() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (id: Uuid) => apiPost<Stocktake>(`/inventory/stocktake/sessions/${id}/finish`),
    onSettled: invalidate,
  });
}

export interface WeighBody {
  code: string;
  measurement_kind?: "gross" | "net";
  gross_weight_g?: DecimalString;
  net_weight_g?: DecimalString;
  container_tare_g?: DecimalString | null;
  session_id?: Uuid | null;
  note?: string;
}

export function useStocktakeWeigh() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: WeighBody) => apiPost<WeighResult>("/inventory/stocktake/weigh", body),
    onSettled: invalidate,
  });
}

// Low stock and reorder points ---------------------------------------------------------------------

export function useLowStock(enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.lowStock,
    queryFn: ({ signal }) => apiGet<LowStock>("/inventory/low-stock", { signal }),
    enabled,
  });
}

export function useReorderPoints() {
  return useQuery({
    queryKey: inventoryKeys.reorderPoints,
    queryFn: ({ signal }) => apiGet<ReorderPoint[]>("/inventory/reorder-points", { signal }),
  });
}

export function useSetReorderPoint() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: ({ materialId, minG, notes }: { materialId: Uuid; minG: DecimalString; notes?: string }) =>
      apiPut<ReorderPoint>(`/inventory/reorder-points/${materialId}`, { min_g: minG, notes: notes ?? "" }),
    onSettled: invalidate,
  });
}

export function useDeleteReorderPoint() {
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (materialId: Uuid) => apiDelete(`/inventory/reorder-points/${materialId}`),
    onSettled: invalidate,
  });
}

// Material search (GET /api/search, materials group) -----------------------------------------------

export async function searchMaterials(query: string, signal?: AbortSignal): Promise<MaterialHit[]> {
  const response = await apiGet<{ results: Record<string, MaterialHit[]> }>("/search", {
    params: { q: query, groups: "materials", limit: 20 },
    signal,
  });
  return response.results.materials ?? [];
}

// Labels (POST /api/labels/render, owned by the labels slice) ---------------------------------------

export type LabelItemKind = "lot" | "location" | "batch";

export interface RenderLabelsBody {
  template_id: string;
  item_kind: LabelItemKind;
  item_ids: Uuid[];
  start_at: number;
  options?: Record<string, unknown>;
}

export interface RenderedLabels {
  document_id: Uuid;
  url: string;
}

export function useRenderLabels() {
  return useMutation({
    mutationFn: (body: RenderLabelsBody) => apiPost<RenderedLabels>("/labels/render", { options: {}, ...body }),
  });
}

// Balance extension point ---------------------------------------------------------------------------

/**
 * A source of weights for the weigh-in and stocktake fields. When an ancestor provides one (the
 * integration wires `useBalance()` from src/devices here), each weight field shows a "Read balance"
 * button. Keyboard-wedge balances (A&D Quick USB) type into the focused field without it.
 */
export interface WeightCapture {
  /** Button text, e.g. "Read FX-123". */
  label: string;
  /** Resolves with a stable reading in grams as a decimal string. */
  capture: () => Promise<DecimalString>;
  /** False while the balance is disconnected; the button is then disabled with `status` as the reason. */
  ready: boolean;
  /** Short status text: "Connected", "Not connected", "Unstable". */
  status?: string;
}

export const WeightCaptureContext = createContext<WeightCapture | null>(null);
